import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { getUser } from '../App';

const QUESTION_COUNT_OPTIONS = [10, 20, 50, 'Alle'];

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

export default function Quiz() {
  const user = getUser();
  const navigate = useNavigate();
  const [allQuestions, setAllQuestions] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [selected, setSelected] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCount, setSelectedCount] = useState(20);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [reporting, setReporting] = useState(false);
  const [reportComment, setReportComment] = useState('');
  const [reportStatus, setReportStatus] = useState(null);

  const handleOpenReport = () => {
    setReporting(true);
    setReportComment('');
    setReportStatus(null);
  };

  const handleCloseReport = () => {
    setReporting(false);
    setReportComment('');
    setReportStatus(null);
  };

  const handleSubmitReport = async (e) => {
    e.preventDefault();
    setReportStatus('submitting');
    try {
      await api.post('/api/reports', {
        questionId: questions[currentIdx].id,
        comment: reportComment
      });
      setReportStatus('success');
      setTimeout(() => {
        setReporting(false);
        setReportStatus(null);
      }, 1500);
    } catch (err) {
      console.error('Error submitting report:', err);
      setReportStatus('error');
    }
  };

  useEffect(() => {
    api.get('/api/questions')
      .then(({ data }) => setAllQuestions(data))
      .catch(() => setError('Vragen konden niet worden geladen. Probeer opnieuw.'))
      .finally(() => setLoading(false));
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleStart = () => {
    const shuffled = shuffle(allQuestions);
    const count = selectedCount === 'Alle' ? shuffled.length : selectedCount;
    const subset = shuffled.slice(0, count);
    setQuestions(subset);
    setTotalQuestions(subset.length);
    setCurrentIdx(0);
    setScore(0);
    setAnsweredCount(0);
    setSelected(null);
    setShowExplanation(false);
    setFinished(false);
    setStarted(true);
  };

  const handleAnswer = (answer) => {
    setSelected(answer);
    setShowExplanation(true);
    if (answer === questions[currentIdx].correctAnswer) {
      setScore((s) => s + 1);
    }
    setAnsweredCount((c) => c + 1);
  };

  const handleNext = () => {
    const remaining = [...questions];
    remaining.splice(currentIdx, 1);
    if (remaining.length === 0) {
      setFinished(true);
      // Save result to backend
      api.post('/api/results', { score: score + (selected === questions[currentIdx]?.correctAnswer ? 0 : 0), total: totalQuestions })
        .catch(() => {}); // fail silently
    } else {
      setQuestions(remaining);
      setCurrentIdx(0);
      setShowExplanation(false);
      setSelected(null);
    }
  };

  const handleRestart = () => {
    setStarted(false);
    setFinished(false);
    setScore(0);
    setAnsweredCount(0);
    setSelected(null);
    setShowExplanation(false);
  };

  const TopBar = () => (
    <div className="quiz-topbar">
      <span className="topbar-welcome">Welkom, {user?.name || user?.email}</span>
      <div className="topbar-actions">
        {user?.role === 'admin' && (
          <Link to="/admin" className="topbar-link">Admin paneel →</Link>
        )}
        <button className="topbar-logout" onClick={handleLogout}>Uitloggen</button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <>
        <TopBar />
        <div className="quiz-container">
          <div className="quiz-card loading-card">
            <div className="spinner" aria-label="Laden"></div>
            <p>Vragen ophalen...</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <TopBar />
        <div className="quiz-container">
          <div className="quiz-card error-card">
            <p className="error-message">{error}</p>
            <button className="btn btn-primary" onClick={() => window.location.reload()}>
              Opnieuw proberen
            </button>
          </div>
        </div>
      </>
    );
  }

  if (finished) {
    const percentage = Math.round((score / totalQuestions) * 100);
    const passed = percentage >= 60;
    // Save the final result (re-save with correct score after last question)
    return (
      <>
        <TopBar />
        <div className="quiz-container">
          <div className="quiz-card results-card">
            <h1>HVAC Quiz</h1>
            <div className={`result-badge ${passed ? 'passed' : 'failed'}`}>
              {passed ? '✓ Geslaagd' : '✗ Niet geslaagd'}
            </div>
            <div className="score-display">
              <span className="score-number">{score}</span>
              <span className="score-divider">/</span>
              <span className="score-total">{totalQuestions}</span>
            </div>
            <p className="score-percentage">{percentage}%</p>
            <p className="score-message">
              {percentage === 100
                ? 'Perfect! Uitstekend gedaan!'
                : passed
                ? 'Goed gedaan! Je hebt de quiz gehaald.'
                : 'Blijf oefenen! Je scoort min. 60% om te slagen.'}
            </p>
            <button className="btn btn-primary" onClick={handleRestart}>
              Opnieuw starten
            </button>
          </div>
        </div>
      </>
    );
  }

  if (!started) {
    return (
      <>
        <TopBar />
        <div className="quiz-container">
          <div className="quiz-card start-card">
            <h1>HVAC Quiz</h1>
            <p className="subtitle">Voorbereiding examen koeltechnieker</p>
            <p className="question-bank-info">{allQuestions.length} vragen beschikbaar</p>
            <div className="count-selector">
              <label htmlFor="question-count">Aantal vragen:</label>
              <div className="count-options" id="question-count">
                {QUESTION_COUNT_OPTIONS.map((opt) => (
                  <button
                    key={opt}
                    className={`count-btn ${selectedCount === opt ? 'active' : ''}`}
                    onClick={() => setSelectedCount(opt)}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
            <button className="btn btn-primary btn-large" onClick={handleStart}>
              Start Quiz
            </button>
          </div>
        </div>
      </>
    );
  }

  const current = questions[currentIdx];
  const progressPercent = Math.round((answeredCount / totalQuestions) * 100);

  return (
    <>
      <TopBar />
      <div className="quiz-container">
        <div className="quiz-card question-card">
          <div className="quiz-header">
            <span className="progress-label">Vraag {answeredCount + 1} / {totalQuestions}</span>
            <span className="question-ref">
              #{current.id}
              <button
                type="button"
                className="btn-report-link"
                onClick={handleOpenReport}
                title="Fout gevonden in deze vraag? Meld het hier"
              >
                ⚠️ Fout melden
              </button>
            </span>
            <span className="score-label">Score: {score}</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progressPercent}%` }}></div>
          </div>

          {!showExplanation ? (
            <>
              <h2 className="question-text">{current.question}</h2>
              <div className="options-list">
                {current.options.map((opt, i) => (
                  <button
                    key={i}
                    className="option-btn"
                    onClick={() => handleAnswer(opt)}
                    disabled={selected !== null}
                  >
                    <span className="option-letter">{String.fromCharCode(65 + i)}</span>
                    {opt}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <h2 className="question-text">{current.question}</h2>
              <div className="feedback-section">
                <div className={`answer-feedback ${selected === current.correctAnswer ? 'correct' : 'incorrect'}`}>
                  {selected === current.correctAnswer ? '✓ Correct!' : '✗ Fout'}
                </div>
                <div className="answer-details">
                  <p>
                    <strong>Jouw antwoord:</strong>{' '}
                    <span className={selected === current.correctAnswer ? 'correct-text' : 'incorrect-text'}>
                      {selected}
                    </span>
                  </p>
                  {selected !== current.correctAnswer && (
                    <p>
                      <strong>Correct antwoord:</strong>{' '}
                      <span className="correct-text">{current.correctAnswer}</span>
                    </p>
                  )}
                </div>
                {current.explanation && (
                  <div className="explanation-box">
                    <strong>Uitleg:</strong>
                    <p>{current.explanation}</p>
                  </div>
                )}
              </div>
              <button className="btn btn-primary" onClick={handleNext}>
                {questions.length === 1 ? 'Resultaten bekijken' : 'Volgende vraag →'}
              </button>
            </>
          )}

          {reporting && (
            <div className="report-modal-overlay" onClick={handleCloseReport}>
              <div className="report-modal" onClick={(e) => e.stopPropagation()}>
                <h3>⚠️ Fout melden bij vraag #{current.id}</h3>
                <p className="report-question-snippet">"{current.question}"</p>
                {reportStatus === 'success' ? (
                  <div className="report-success-msg">✓ Bedankt voor je melding! We kijken het na.</div>
                ) : (
                  <form onSubmit={handleSubmitReport}>
                    <label htmlFor="report-textarea-v2">Wat klopt er niet? (optioneel):</label>
                    <textarea
                      id="report-textarea-v2"
                      rows="3"
                      placeholder="Bijv. antwoord klopt niet, typfout in optie B..."
                      value={reportComment}
                      onChange={(e) => setReportComment(e.target.value)}
                      autoFocus
                    />
                    {reportStatus === 'error' && (
                      <p className="report-error-msg">Versturen mislukt. Probeer opnieuw.</p>
                    )}
                    <div className="report-actions">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleCloseReport}
                        disabled={reportStatus === 'submitting'}
                      >
                        Annuleren
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={reportStatus === 'submitting'}
                      >
                        {reportStatus === 'submitting' ? 'Versturen...' : 'Versturen'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
